<template>
  <div class="flex flex-col gap-4">
    <SearchInput
      v-model="searchQuery"
      label="Buscar política"
      density="compact"
      data-testid="qualification-doc-search-input"
      class="w-[320px]"
    />

    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2 text-sm text-gray-600">
        <p class="font-semibold text-black">Legenda:</p>
        <span
          class="w-2.5 h-2.5 rounded-full border border-green-500 bg-green-100 inline-block"
        ></span>
        Em vigência
      </div>

      <!--
        `attach="#app"` é obrigatório, não estético: o Tailwind deste projeto é gerado
        com `important: '#app'` (tailwind.config.js), então toda utility sai como
        `#app .p-4 { … }`. O destino padrão do overlay é `body > .v-overlay-container`,
        irmão de `#app` — lá dentro nenhuma classe Tailwind casa, e o card (mais o
        DsSwitch, que é inteiro feito de utilities) perde todo o estilo. Teleportando
        para dentro de `#app` a cascata volta. `attach` só troca o alvo do teleport;
        quem tornaria o overlay `position: absolute` seria `absolute`/`contained`, então
        a ancoragem no ativador continua igual.
      -->
      <v-menu
        v-model="showLinkCard"
        :close-on-content-click="false"
        location="bottom end"
        offset="8"
        attach="#app"
      >
        <template #activator="{ props: menuProps }">
          <outline-button
            v-bind="menuProps"
            text="Vincular política"
            height="32px"
            class="text-sm !w-auto !px-4 !py-1"
            data-testid="qualification-doc-link-policy-button"
          />
        </template>

        <div
          class="bg-white border border-gray-200 rounded-lg shadow-md p-4 w-[320px]"
          data-testid="qualification-doc-link-policy-card"
        >
          <div class="flex justify-between items-center mb-3">
            <h3 class="text-base font-semibold text-gray-800">Vincular política</h3>
            <button
              class="text-gray-400 hover:text-gray-700"
              data-testid="qualification-doc-link-policy-close-button"
              @click="handleCloseLinkCard"
            >
              ✕
            </button>
          </div>

          <div class="flex flex-col gap-4">
            <v-autocomplete
              v-model="selectedLinkPolicyId"
              :items="availablePolicies"
              item-title="policyName"
              item-value="id"
              label="Selecionar uma política"
              variant="outlined"
              density="compact"
              hide-details
              :loading="isLoadingAvailablePolicies"
              data-testid="qualification-doc-link-policy-select"
            />

            <GenericInput
              v-model="linkStartDate"
              type="date"
              label="Início da vigência"
              density="compact"
              hide-details
              data-testid="qualification-doc-link-start-date"
            />

            <GenericInput
              v-model="linkEndDate"
              type="date"
              label="Fim da vigência"
              density="compact"
              hide-details
              :disabled="isLinkIndefiniteEnd"
              data-testid="qualification-doc-link-end-date"
            />

            <DsSwitch
              v-model="isLinkIndefiniteEnd"
              label="Fim de vigência indeterminado"
              data-testid="qualification-doc-link-indefinite-toggle"
              @update:model-value="handleLinkIndefiniteToggle"
            />

            <FilledButton
              text="Incluir política"
              color="primary30"
              height="40px"
              :loading="isLinkSubmitting"
              data-testid="qualification-doc-link-submit-button"
              @click="handleSubmitLink"
            />
          </div>
        </div>
      </v-menu>
    </div>

    <QualificationTransferTableSkeleton v-if="isLoading" />

    <div
      v-else-if="policies.length === 0"
      class="flex flex-col items-center justify-center py-12 text-center text-gray-500"
      data-testid="qualification-doc-empty-state"
    >
      <v-icon :icon="mdiEmailOpenMultipleOutline" size="48" color="grey-lighten-1" class="mb-3" />
      <p class="text-base font-semibold text-gray-700">Nenhuma política encontrada</p>
      <p class="text-sm text-gray-500 mt-1">
        {{ searchQuery ? 'Tente buscar com outro termo.' : 'Vincule uma política para começar.' }}
      </p>
    </div>

    <TableModular
      v-else
      :rows="filteredPolicies"
      :columns="columns"
      actions-label="Ações"
      data-testid="qualification-doc-table"
    >
      <template #column-period="{ row }">
        {{ formatIsoDateOnlyBR(row.startDate) }} à {{ formatIsoDateOnlyBR(row.endDate) }}
      </template>

      <template #actions="{ row }">
        <button
          data-testid="qualification-doc-edit-button"
          class="text-primary30"
          @click="handleEditPolicy(row as QualificationDocumentalLink)"
        >
          <v-icon size="20" :icon="mdiPencilOutline" />
        </button>
        <v-switch
          :model-value="row.isActive"
          color="primary30"
          density="compact"
          hide-details
          data-testid="qualification-doc-toggle-button"
          @update:model-value="handleToggle(row as QualificationDocumentalLink, $event as boolean)"
        />
      </template>
    </TableModular>

    <QualificationDocPolicyModal
      :modal-active="showEditModal"
      :policy="selectedPolicy ?? undefined"
      :schedule-id="props.scheduleId"
      :link-id="selectedLinkId ?? undefined"
      @close="handleCloseEditModal"
      @saved="handlePolicySaved"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { mdiPencilOutline, mdiEmailOpenMultipleOutline } from '@mdi/js'
import { useToast } from 'vue-toastification'
import SearchInput from '@/components/designSystem/Inputs/SearchInput.vue'
import GenericInput from '@/components/designSystem/Inputs/GenericInput.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import TableModular from '@/components/designSystem/Table/TableModular/TableModular.vue'
import DsSwitch from '@/components/designSystem/Switch/index.vue'
import QualificationTransferTableSkeleton from '@/components/designSystem/Skeleton/QualificationTransferTableSkeleton.vue'
import Network from '@/Network'
import { useErrorToast } from '@/composables/useErrorToast'
import { formatIsoDateOnlyBR } from '@/utils/date'
import type {
  QualificationDocumentalPolicyItem,
  QualificationDocumentalLink
} from '@/Network/Types/Responses/QualificationDocumentalResponses'
import QualificationDocPolicyModal from './QualificationDocPolicyModal.vue'

const props = defineProps<{
  scheduleId: number
}>()

// `toast` fica apenas para o sucesso e para as validações locais do formulário;
// todo erro vindo da API passa pelo `notifyError` (rule `error-handling.md`).
const toast = useToast()
const { notifyError } = useErrorToast()
const isLoading = ref(false)
const searchQuery = ref('')
const policies = ref<QualificationDocumentalLink[]>([])

const columns = [
  { key: 'policyName', label: 'Nome' },
  { key: 'period', label: 'Período' },
  { key: 'linkedBy', label: 'Vinculado por' }
]

const filteredPolicies = computed(() => {
  if (!searchQuery.value) return policies.value
  const query = searchQuery.value.toLowerCase()
  return policies.value.filter((p) => p.policyName.toLowerCase().includes(query))
})

const fetchPolicies = async () => {
  try {
    isLoading.value = true
    const response = await Network.qualificationDocumentalPolicy.listScheduleLinks(props.scheduleId)
    policies.value = response.data
  } catch (e) {
    notifyError(e, 'Não foi possível carregar as políticas de qualificação.')
  } finally {
    isLoading.value = false
  }
}

const handleToggle = async (link: QualificationDocumentalLink, value: boolean) => {
  const index = policies.value.findIndex((l) => l.id === link.id)
  if (index === -1) return
  policies.value[index] = { ...policies.value[index], isActive: value }
  try {
    await Network.qualificationDocumentalPolicy.toggleActive(link.policyId, { isActive: value })
  } catch (e) {
    policies.value[index] = { ...policies.value[index], isActive: !value }
    notifyError(e, 'Não foi possível alterar o status da política.')
  }
}

const showEditModal = ref(false)
const selectedPolicy = ref<QualificationDocumentalPolicyItem | null>(null)
const selectedLinkId = ref<number | null>(null)

const showLinkCard = ref(false)
const selectedLinkPolicyId = ref<number | null>(null)
const linkStartDate = ref('')
const linkEndDate = ref('')
const isLinkIndefiniteEnd = ref(false)
const isLinkSubmitting = ref(false)
const availablePolicies = ref<QualificationDocumentalPolicyItem[]>([])
const isLoadingAvailablePolicies = ref(false)

const resetLinkForm = () => {
  selectedLinkPolicyId.value = null
  linkStartDate.value = ''
  linkEndDate.value = ''
  isLinkIndefiniteEnd.value = false
}

// O card e o conteudo do v-menu: o fechamento programatico (botao ✕ ou apos
// submeter) nao emite `update:model-value`, por isso o watch — ele cobre os dois
// sentidos (abrir carrega as politicas, fechar limpa o formulario).
watch(showLinkCard, (open) => {
  if (open) {
    void loadAvailablePolicies()
  } else {
    resetLinkForm()
  }
})

const loadAvailablePolicies = async () => {
  try {
    isLoadingAvailablePolicies.value = true
    const response = await Network.qualificationDocumentalPolicy.list()
    availablePolicies.value = response.data
  } catch (e) {
    notifyError(e, 'Não foi possível carregar as políticas disponíveis.')
  } finally {
    isLoadingAvailablePolicies.value = false
  }
}

const handleCloseLinkCard = () => {
  showLinkCard.value = false
}

const handleLinkIndefiniteToggle = (value: boolean) => {
  if (value) linkEndDate.value = ''
}

const handleSubmitLink = async () => {
  if (!selectedLinkPolicyId.value) {
    toast.error('Selecione uma política.')
    return
  }
  if (!linkStartDate.value) {
    toast.error('Informe a data de início da vigência.')
    return
  }
  if (!isLinkIndefiniteEnd.value && !linkEndDate.value) {
    toast.error('Informe a data de fim da vigência ou marque como indeterminado.')
    return
  }

  try {
    isLinkSubmitting.value = true
    await Network.qualificationDocumentalPolicy.createScheduleLink(props.scheduleId, {
      policyId: selectedLinkPolicyId.value,
      startDate: linkStartDate.value,
      endDate: isLinkIndefiniteEnd.value ? undefined : linkEndDate.value || undefined
    })
    toast.success('Política vinculada com sucesso!')
    handleCloseLinkCard()
    await fetchPolicies()
  } catch (e) {
    notifyError(e, 'Não foi possível vincular a política.')
  } finally {
    isLinkSubmitting.value = false
  }
}

const handleEditPolicy = (link: QualificationDocumentalLink) => {
  selectedLinkId.value = link.id
  selectedPolicy.value = {
    id: link.policyId,
    policyName: link.policyName,
    company: '',
    startDate: link.startDate,
    endDate: link.endDate,
    createdBy: '',
    isActive: link.isActive
  }
  showEditModal.value = true
}

const handleCloseEditModal = () => {
  showEditModal.value = false
  selectedPolicy.value = null
  selectedLinkId.value = null
}

const handlePolicySaved = async () => {
  handleCloseEditModal()
  await fetchPolicies()
}

onMounted(fetchPolicies)
</script>
